import { CommonMFEShellSpiAdapter } from '@ingenico/util-mfe-shell';
import type { EnvironmentData } from '@ingenico/util-mfe-shell/env';
import { CommonAndProjectEnvDataAndSchemas } from '@ingenico/util-mfe-shell/env';
import type { Permission, RoleName, EntityType } from '@ingenico/react-section-utils';
import { behaviorOf, type BehaviorObservable } from '@ingenico/util-common';
import type { DeviceManagementHostInfo, DeviceManagementSpiPort } from '../../../domain/domain/hexa/ports/spi.model';
import type { LazyContainerApi } from '@ingenico/util-mfe';
import { inject, injectable } from 'inversify';
import type { DeviceManagementCapabilities } from '../../../domain/domain/models/capabilities.model';
import { DeviceManagementEnvDataAndSchemas, DeviceManagementEnvKey, ProvisioningEnvKey } from '../../config.const';
import { StandalonePermissionOverridesService } from '../../services/standalonePermissionOverrides.service';
import { UserTypeSwitchService } from '../../services/userTypeSwitch.service';
import { appContainer } from '../appContainer.const';
import type { MfeConfiguration } from '../hexa.model';

@injectable()
export class DeviceManagementSpiAdapter
  extends CommonMFEShellSpiAdapter<DeviceManagementCapabilities>
  implements DeviceManagementSpiPort
{
  private readonly lazyContainerApi: LazyContainerApi<MfeConfiguration>;

  constructor(
    @inject<LazyContainerApi<MfeConfiguration>>('LazyContainerApi')
    lazyContainerApi: LazyContainerApi<MfeConfiguration>,
    @inject<EnvironmentData<CommonAndProjectEnvDataAndSchemas<DeviceManagementEnvDataAndSchemas>>>('EnvironmentValues')
    private readonly environmentData: EnvironmentData<
      CommonAndProjectEnvDataAndSchemas<DeviceManagementEnvDataAndSchemas>
    >,
  ) {
    super(appContainer);
    this.lazyContainerApi = lazyContainerApi;
  }

  getHostInfo(): DeviceManagementHostInfo {
    return { kind: 'standalone' };
  }

  getGoogleMapConfig() {
    return {
      googleMapUrl: this.environmentData[ProvisioningEnvKey.GmapUrl],
      googleMapApi: this.environmentData[ProvisioningEnvKey.GmapApi],
      googleMapKey: this.environmentData[ProvisioningEnvKey.GmapKey],
    };
  }

  // eslint-disable-next-line max-lines-per-function
  processCapabilities(permissions: Permission[], roles: RoleName[]): DeviceManagementCapabilities {
    const currentUserType = UserTypeSwitchService.getCurrentUserType();

    // Keep the latest parent-provided permissions for standalone override UI initialization.
    StandalonePermissionOverridesService.setInterceptedBasePermissions(currentUserType, permissions);

    // Apply permission overrides from localStorage for standalone testing
    const resolvedPermissions = StandalonePermissionOverridesService.getResolvedPermissions(
      permissions,
      currentUserType,
    );

    return {
      apps: {
        showInGlobalNavigation: resolvedPermissions.includes('dm.appRead'),
        list: resolvedPermissions.includes('dm.appRead'),
        create: resolvedPermissions.includes('dm.appWrite'),
        isDeveloperPortal: UserTypeSwitchService.isDeveloperUser(),
        detail: resolvedPermissions.includes('dm.appRead'),
        edit: resolvedPermissions.includes('dm.appWrite'),
        version: {
          list: resolvedPermissions.includes('dm.appVersionRead'),
          create: resolvedPermissions.includes('dm.appVersionWrite'),
          detail: resolvedPermissions.includes('dm.appVersionRead'),
          edit: resolvedPermissions.includes('dm.appVersionWrite'),
          enableDownloadRules: this.environmentData[DeviceManagementEnvKey.EnableDownloadRules],
          enableApkParsing: true,
        },
        showOrganizationsTab: resolvedPermissions.includes('prov.orgRead'),
        publish: resolvedPermissions.includes('dm.appWrite'),
        shouldEnableDownloadRules: this.environmentData[DeviceManagementEnvKey.EnableDownloadRules],
        shouldEnableAppLinkDelivery: UserTypeSwitchService.isDeveloperUser(),
      },
      os: {
        showInGlobalNavigation: resolvedPermissions.includes('dm.osRead'),
        list: resolvedPermissions.includes('dm.osRead'),
        create: resolvedPermissions.includes('dm.osWrite'),
        detail: resolvedPermissions.includes('dm.osRead'),
        edit: resolvedPermissions.includes('dm.osWrite'),
        manageVersions: resolvedPermissions.includes('dm.osVersionRead'),
        showOrganizationsTab: resolvedPermissions.includes('prov.orgRead'),
        publish: resolvedPermissions.includes('dm.osWrite'),
        version: {
          create: resolvedPermissions.includes('dm.osVersionWrite'),
        },
      },
      systemPackages: {
        showInGlobalNavigation: resolvedPermissions.includes('dm.systemPackageRead'),
        list: resolvedPermissions.includes('dm.systemPackageRead'),
        create: resolvedPermissions.includes('dm.systemPackageWrite'),
        detail: resolvedPermissions.includes('dm.systemPackageRead'),
        edit: resolvedPermissions.includes('dm.systemPackageWrite'),
        version: {
          list: resolvedPermissions.includes('dm.systemPackageVersionRead'),
          detail: resolvedPermissions.includes('dm.systemPackageVersionRead'),
          create: resolvedPermissions.includes('dm.systemPackageVersionWrite'),
          edit: resolvedPermissions.includes('dm.systemPackageVersionWrite'),
          enableDownloadRules: this.environmentData[DeviceManagementEnvKey.EnableDownloadRules],
        },
        showOrganizationsTab: resolvedPermissions.includes('prov.orgRead'),
        publish: resolvedPermissions.includes('dm.systemPackageWrite'),
        shouldEnableDownloadRules: this.environmentData[DeviceManagementEnvKey.EnableDownloadRules],
      },
      resources: {
        showInGlobalNavigation:
          resolvedPermissions.some((permission) => permission === 'dm.resourceRead') &&
          this.environmentData[DeviceManagementEnvKey.EnableFileAsConfig],
        list:
          resolvedPermissions.some((permission) => permission === 'dm.resourceRead') &&
          this.environmentData[DeviceManagementEnvKey.EnableFileAsConfig],
        create:
          resolvedPermissions.some((permission) => permission === 'dm.resourceRead') &&
          this.environmentData[DeviceManagementEnvKey.EnableFileAsConfig] &&
          resolvedPermissions.some((permission) => permission === 'dm.resourceWrite'),
        edit:
          resolvedPermissions.some((permission) => permission === 'dm.resourceWrite') &&
          this.environmentData[DeviceManagementEnvKey.EnableFileAsConfig],
        delete:
          resolvedPermissions.some((permission) => permission === 'dm.resourceDelete') &&
          this.environmentData[DeviceManagementEnvKey.EnableFileAsConfig],
      },
      task: {
        showInGlobalNavigation: resolvedPermissions.includes('task.read'),
        list: resolvedPermissions.includes('task.read'),
        detailOverview: resolvedPermissions.includes('task.read'),
        deviceChanges: resolvedPermissions.includes('task.read'),
        retry: resolvedPermissions.includes('task.write'),
      },
      rki: {
        showInGlobalNavigation: resolvedPermissions.includes('dm.deviceGroupRead'),
        detail: resolvedPermissions.includes('dm.deviceGroupRead'),
        showOrganizationsTab: resolvedPermissions.includes('prov.orgRead'),
        addKeyset: resolvedPermissions.includes('prov.orgWrite'),
        shouldEnableKeysetSegregation: this.environmentData[DeviceManagementEnvKey.EnableKeysetSegregation],
        canManageKeysets:
          resolvedPermissions.includes('dm.deviceGroupWrite' as Permission) &&
          this.environmentData[DeviceManagementEnvKey.EnableKeysetSegregation],
      },
      configProfile: {
        useDeploymentConfigurationFlow:
          this.environmentData[DeviceManagementEnvKey.EnableSmartfieldValuesComputedConfiguration],
        showInGlobalNavigation: resolvedPermissions.includes('dm.configProfileRead'),
        list: resolvedPermissions.includes('dm.configProfileRead'),
        create: resolvedPermissions.includes('dm.configProfileWrite'),
        detail: resolvedPermissions.includes('dm.configProfileRead'),
        edit: resolvedPermissions.includes('dm.configProfileWrite'),
        duplicate: resolvedPermissions.includes('dm.configProfileWrite'),
        delete: resolvedPermissions.includes('dm.configProfileDelete'),
        assignGroup:
          resolvedPermissions.includes('dm.deploymentAutomatorWrite') &&
          resolvedPermissions.includes('conf.dynamicGroupRead'),
        displayGroupsTab:
          resolvedPermissions.includes('dm.deploymentAutomatorRead') &&
          resolvedPermissions.includes('conf.dynamicGroupRead'),
        templateParameterOptions: this.environmentData[DeviceManagementEnvKey.EnableTemplateParametersOptions],
        enableFormBuilderDynamicChoice: this.environmentData[DeviceManagementEnvKey.EnableFormBuilderDynamicChoice],
        enableConfigProfilePut: this.environmentData[DeviceManagementEnvKey.EnableConfigProfilePut],
        skipDefaultTemplateValues:
          this.environmentData[DeviceManagementEnvKey.EnableFormBuilderSkipDefaultTemplateValues],
        keysetSegregation: this.environmentData[DeviceManagementEnvKey.EnableKeysetSegregation],
        enableConfigProfileOrdering: this.environmentData[DeviceManagementEnvKey.EnableConfigProfileOrdering],
        showSecondaryDeviceWarning: this.environmentData[DeviceManagementEnvKey.EnableDmMultiMerchant],
      },
      deployments: {
        showInGlobalNavigation: true,
        list: resolvedPermissions.includes('dm.deploymentRequestRead'),
        detail: resolvedPermissions.includes('dm.deploymentRequestRead'),
        create: resolvedPermissions.includes('dm.deploymentRequestWrite'),
        delete: resolvedPermissions.includes('dm.deploymentRequestWrite'),
        edit: resolvedPermissions.includes('dm.deploymentRequestWrite'),
        showDeploymentsTab: resolvedPermissions.includes('dm.deploymentRequestRead'),
        showTargetDevicesTab: resolvedPermissions.includes('dm.deploymentUnitRead'),
        showDeployButton: resolvedPermissions.includes('dm.deploymentRequestWrite'),
        canViewOutputFile:
          resolvedPermissions.includes('dm.configModuleRead') &&
          this.environmentData[ProvisioningEnvKey.EnableViewOutputFeature],
        canWriteDeleteDeploy: this.environmentData[DeviceManagementEnvKey.EnableAdl]
          ? resolvedPermissions.includes('dm.deploymentRequestUninstallWrite')
          : resolvedPermissions.includes('dm.deploymentRequestWrite'),
        canWriteConfigurationDeploy: this.environmentData[DeviceManagementEnvKey.EnableAdl]
          ? resolvedPermissions.includes('dm.deploymentRequestUninstallWrite')
          : resolvedPermissions.includes('dm.deploymentRequestWrite'),
        canEditScheduling: this.environmentData[DeviceManagementEnvKey.EnableAdl],
        canUninstallSoftware: this.environmentData[DeviceManagementEnvKey.EnableAdl]
          ? resolvedPermissions.includes('dm.deploymentRequestUninstallWrite')
          : resolvedPermissions.includes('dm.deploymentRequestWrite'),
        isFeatureFlippingEnableAdlOn: this.environmentData[DeviceManagementEnvKey.EnableAdl],
        hasUninstallPermission: resolvedPermissions.includes('dm.deploymentRequestUninstallWrite'),
        isProgressiveDeploymentEnabled:
          this.environmentData[DeviceManagementEnvKey.EnableProgressiveDeployment] ?? false,
        isDmMultiMerchantEnabled: this.environmentData[DeviceManagementEnvKey.EnableDmMultiMerchant] ?? false,
        isSkipBlockedDeploymentEnabled:
          this.environmentData[DeviceManagementEnvKey.EnableSkipBlockedDeployment] ?? false,
      },
      smartFields: {
        showInGlobalNavigation: resolvedPermissions.includes('conf.smartFieldRead'),
        list: resolvedPermissions.includes('conf.smartFieldRead'),
        create: resolvedPermissions.includes('conf.smartFieldWrite'),
        edit: resolvedPermissions.includes('conf.smartFieldWrite'),
        detail: resolvedPermissions.includes('conf.smartFieldRead'),
        delete: resolvedPermissions.includes('conf.smartFieldDelete'),
        overrideRead: resolvedPermissions.includes('conf.smartFieldOverrideRead'),
        overrideWrite: resolvedPermissions.includes('conf.smartFieldOverrideWrite'),
        overrideDelete: resolvedPermissions.includes('conf.smartFieldOverrideDelete'),
        showCategoryUi: this.environmentData[DeviceManagementEnvKey.EnableSmartfieldCategoryFeature] === true,
        listCategories:
          this.environmentData[DeviceManagementEnvKey.EnableSmartfieldCategoryFeature] &&
          resolvedPermissions.includes('conf.smartFieldCategoryRead'),
        editCategory:
          this.environmentData[DeviceManagementEnvKey.EnableSmartfieldCategoryFeature] &&
          resolvedPermissions.includes('conf.smartFieldCategoryWrite'),
        deleteCategory:
          this.environmentData[DeviceManagementEnvKey.EnableSmartfieldCategoryFeature] &&
          resolvedPermissions.includes('conf.smartFieldCategoryDelete'),
      },
      modules: {
        showInGlobalNavigation: true,
        list: resolvedPermissions.includes('dm.configModuleRead'),
        create: resolvedPermissions.includes('dm.configModuleWrite'),
        edit: resolvedPermissions.includes('dm.configModuleWrite'),
        detail: resolvedPermissions.includes('dm.configModuleRead'),
        delete: resolvedPermissions.includes('dm.configModuleDelete'),
        enableParametersImportExport: this.environmentData[DeviceManagementEnvKey.EnableParametersImportExport],
      },
      services: {
        showInGlobalNavigation:
          this.environmentData[DeviceManagementEnvKey.EnableServicesFeature] &&
          resolvedPermissions.includes('svc.subscriptionRead') &&
          resolvedPermissions.includes('svc.serviceConfigurationRead'),
        list:
          this.environmentData[DeviceManagementEnvKey.EnableServicesFeature] &&
          resolvedPermissions.includes('svc.subscriptionRead') &&
          resolvedPermissions.includes('svc.serviceConfigurationRead'),
        edit:
          resolvedPermissions.includes('svc.serviceConfigurationWrite') &&
          this.environmentData[DeviceManagementEnvKey.EnableServicesFeature],
        detail:
          resolvedPermissions.includes('svc.serviceConfigurationRead') &&
          this.environmentData[DeviceManagementEnvKey.EnableServicesFeature],
      },
      logicalDevice: {
        edit: resolvedPermissions.includes('prov.logDevWrite'),
        deviceStateRead: resolvedPermissions.includes('dm.deviceStateRead'),
        enableGeofencing: this.environmentData[ProvisioningEnvKey.EnableGeofencing],
        isDeviceBlockFeatureEnabled: this.environmentData[DeviceManagementEnvKey.EnableDeviceBlock],
        showScreenShare: resolvedPermissions.includes('dm.screenShareCreate'),
        configProfile: {
          enableOrdering: this.environmentData[DeviceManagementEnvKey.EnableConfigProfileOrdering],
        },
        msSyncConfiguration:
          resolvedPermissions.includes('dm.deploymentRequestWrite') &&
          this.environmentData[ProvisioningEnvKey.EnableMsSyncConfiguration],
        list: resolvedPermissions.includes('prov.logDevRead'),
        detail: resolvedPermissions.includes('prov.logDevRead'),
        showDeviceChangesTab: resolvedPermissions.includes('dm.deploymentUnitRead'),
        logcat: {
          read:
            resolvedPermissions.includes('dm.deviceLogcatRead') &&
            this.environmentData[DeviceManagementEnvKey.EnableDeviceLog],

          edit:
            resolvedPermissions.includes('dm.deviceLogcatWrite') &&
            this.environmentData[DeviceManagementEnvKey.EnableDeviceLog],
        },
        configuration: {
          read: resolvedPermissions.includes('dm.configProfileRead'),
        },
        delete: resolvedPermissions.includes('prov.logDevDelete'),
        showDeviceDiagnosticsTab:
          this.environmentData[ProvisioningEnvKey.EnableDeviceDiagnosticsFeature] &&
          resolvedPermissions.includes('dm.deviceDiagRead'),
        detach: resolvedPermissions.includes('prov.physDevAttachDetach'),
        canBlockUnblockDevice:
          resolvedPermissions.includes('prov.physDevBlockUnblock') &&
          this.environmentData[DeviceManagementEnvKey.EnableDeviceBlock],
        syncConfiguration:
          resolvedPermissions.includes('dm.deploymentRequestWrite') &&
          this.environmentData[ProvisioningEnvKey.EnableSyncConfiguration],
        canViewOutputFile:
          resolvedPermissions.includes('dm.configModuleRead') &&
          this.environmentData[ProvisioningEnvKey.EnableViewOutputFeature],
        showDeviceHistoryTab: resolvedPermissions.includes('dm.deviceStateRead'),
        showScreenLockingStatus:
          resolvedPermissions.includes('dm.deviceScreenLockingWrite') &&
          this.environmentData[DeviceManagementEnvKey.EnableDeviceBlock],
      },
      sharing: {
        details: true,
        create: resolvedPermissions.includes('dm.catalogShare' as Permission),
      },
      dynamicGroups: {
        displaySubOrganizationInformation: resolvedPermissions.includes('conf.dynamicGroupRead'),
      },
    };
  }

  getEntityType(): BehaviorObservable<EntityType> {
    return behaviorOf(UserTypeSwitchService.getCurrentUserType() === 'tenant' ? 'tenant' : 'organization');
  }
}
